import { type Battle, type BattleCopy, getItemRarity } from "@loot-os/api-contract";
import type {
  BattleResultItem,
  BattleResultLane,
} from "@loot-os/design-system/domain/battles/battleTypes";
import { rarityTier } from "@loot-os/design-system/domain/rarity";

import { toSeatView } from "../../shared/battleViews.js";

/**
 * What each seat has pulled so far: a round is shown for every seat once the page has shown it
 * (`throughRound`), and for the current round seat by seat as each reel lands (`currentRound` +
 * `settledLanes`), as boxed lights the cards up. Each pull is tiered against its round's price.
 */
export function resultLanes({
  battle,
  throughRound,
  currentRound,
  settledLanes,
  viewerEndUserId,
  showProfile,
  words,
}: {
  battle: Battle;
  /** The last round whose results every seat shows; -1 for none. */
  throughRound: number;
  /** The round in progress, whose landed seats also show; -1 for none. */
  currentRound: number;
  settledLanes: ReadonlySet<number>;
  viewerEndUserId: string | null | undefined;
  showProfile: (endUserId: string) => void;
  words: BattleCopy;
}): BattleResultLane[] {
  return battle.seats.map((seat) => {
    const lastShown =
      currentRound > throughRound && settledLanes.has(seat.seatIndex) ? currentRound : throughRound;
    const items = Array.from({ length: battle.roundCount }, (_, round): BattleResultItem | null => {
      if (round > lastShown) return null;
      const item = battle.results[round]?.find((o) => o.seatIndex === seat.seatIndex)?.item;
      if (!item) return null;
      const roundPrice = battle.rounds[round]?.priceCents ?? battle.priceCents;
      return {
        name: item.name,
        imageUrl: item.imageUrl,
        value: item.value,
        tier: rarityTier(getItemRarity(item.value, roundPrice)),
      };
    });
    return {
      seat: toSeatView(seat, viewerEndUserId, showProfile, words),
      items,
      totalCents: items.reduce((sum, item) => sum + (item?.value ?? 0), 0),
    };
  });
}
